// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Bounce, toast } from 'react-toastify';
import {
  requestNotificationPermission,
  onForegroundMessage,
} from '../utils/firebase-messaging';
import { updateUser } from '../api/userApi';

// Default shape for context
const AuthContext = createContext({
  userData: null,
  notifications: [],
  authenticate: () => {},
  logout: () => {},
  setUserData: () => {},
  fetchNotifications: () => {},
  refreshNotificationToken: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Load stored userData from localStorage
  const [userData, setUserData] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [notifications, setNotifications] = useState([]);

  // Ref to ensure token refresh only runs once per session
  const initCalledRef = useRef(false);

  // Fetch notifications (stubbed)
  const loadNotifications = useCallback(async (userId) => {
    try {
      // TODO: fetch real notifications
      const notifData = [];
      setNotifications(notifData);
    } catch (error) {
      console.error('Error loading notifications', error);
    }
  }, []);

  // Request & register FCM token, subscribe to messages
  const refreshNotificationToken = useCallback(async () => {
    if (!userData) return;
    try {
      const token = await requestNotificationPermission();
      // Update backend
      const updated = await updateUser(userData.id, { fcm_token: token });
      // Update context and storage
      setUserData(updated);
    } catch (error) {
      console.error('Error refreshing FCM token', error);
    }
  }, [userData]);

  // Effect: persist userData, load notifications, and initiate token refresh once
  useEffect(() => {
    if (userData) {
      // Persist
      localStorage.setItem('user', JSON.stringify(userData));
      // Load notifications
      loadNotifications(userData.id);
      // First-time init of FCM token & foreground messages
      if (!initCalledRef.current) {
        initCalledRef.current = true;
        refreshNotificationToken();
      }
    } else {
      localStorage.removeItem('user');
      setNotifications([]);
    }
  }, [userData, loadNotifications, refreshNotificationToken]);

  // Auth handlers
  const authenticate = useCallback((user) => setUserData(user), []);
  const logout = useCallback(() => setUserData(null), []);

  const value = useMemo(
    () => ({
      userData,
      notifications,
      authenticate,
      logout,
      setUserData,
      fetchNotifications: loadNotifications,
      refreshNotificationToken,
    }),
    [userData, notifications, loadNotifications, refreshNotificationToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
